import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import RetroConfetti from './RetroConfetti';
import './SlotMachine.css';

const letters = 'INVITED'.split('');
const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const HeartIcon = ({ className }) => (
  <svg className={`inline-block ${className}`} width="24" height="24" viewBox="0 0 64 53" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M29.6992 10H34.3008V3.3008H41.6992V0H50.6992V3.8008H57.3984V10H64V26H57.3984V34.3984H50.6992V43.3984H41.6992V49.1015H34.3008V52.9999H29.6992V49.1015H22.3008V43.3984H13.3008V34.3984H6.6016V26H0V10H6.6016V3.8008H13.3008V0H22.3008V3.3008H29.6992V10Z" fill="currentColor"/>
  </svg>
);

const AudioButton = ({ audioEnabled, isMuted, onToggle }) => (
  <motion.div
    className="audio-button-container"
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <motion.button
      onClick={onToggle}
      className="audio-button"
      aria-label={audioEnabled ? (isMuted ? "Unmute Audio" : "Mute Audio") : "Start Audio"}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      animate={{ scale: [1, 1.05, 1] }}
      transition={{ repeat: Infinity, duration: 2 }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        {audioEnabled && isMuted ? (
          <>
            <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </>
        ) : (
          <>
            <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M15.54 8.46C16.4774 9.39764 17.0039 10.6692 17.0039 11.995C17.0039 13.3208 16.4774 14.5924 15.54 15.53" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M18.24 5.76C19.9747 7.49468 20.9311 9.84084 20.9311 12.285C20.9311 14.7292 19.9747 17.0753 18.24 18.81" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </>
        )}
      </svg>
    </motion.button>
    <span className="audio-button-label">
      {audioEnabled ? (isMuted ? "UNMUTE" : "MUTE") : "PLAY"}
    </span>
  </motion.div>
);

const SlotMachine = () => {
  const [loading, setLoading] = useState(true);
  const [spinning, setSpinning] = useState(false);
  const [slots, setSlots] = useState(Array(7).fill(''));
  const [currentPage, setCurrentPage] = useState('slot');
  const [spinComplete, setSpinComplete] = useState(false);
  const [isExploding, setIsExploding] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const spinButtonRef = useRef(null);
  const audioContextRef = useRef(null);
  const backgroundAudioRef = useRef(null);
  const winAudioRef = useRef(null);
  const clickAudioRef = useRef(null);
  const slotAudioRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 3000);
    
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    backgroundAudioRef.current = new Audio('/tune.wav');
    backgroundAudioRef.current.loop = true;

    const loadAudioBuffer = async (url) => {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      return await audioContextRef.current.decodeAudioData(arrayBuffer);
    };

    Promise.all([
      loadAudioBuffer('/win.wav'),
      loadAudioBuffer('/click.wav'),
      loadAudioBuffer('/slot.wav')
    ]).then(([winBuffer, clickBuffer, slotBuffer]) => {
      winAudioRef.current = { buffer: winBuffer, source: null };
      clickAudioRef.current = { buffer: clickBuffer, source: null };
      slotAudioRef.current = { buffer: slotBuffer, source: null };
    });

    return () => {
      clearTimeout(timer);
      if (backgroundAudioRef.current) {
        backgroundAudioRef.current.pause();
        backgroundAudioRef.current.currentTime = 0;
      }
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  const playAudio = (audioRef) => {
    if (audioEnabled && !isMuted && audioContextRef.current && audioRef.current) {
      if (audioRef.current.source) {
        audioRef.current.source.stop();
      }
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioRef.current.buffer;
      source.connect(audioContextRef.current.destination);
      source.start();
      audioRef.current.source = source;
    }
  };

  const toggleAudio = () => {
    if (!audioEnabled) {
      setAudioEnabled(true);
      setIsMuted(false);
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().then(() => {
          backgroundAudioRef.current.play();
        });
      } else {
        backgroundAudioRef.current.play();
      }
    } else {
      setIsMuted((prevMuted) => {
        if (prevMuted) {
          backgroundAudioRef.current.play();
        } else {
          backgroundAudioRef.current.pause();
        }
        return !prevMuted;
      });
    }
  };

  const playClickSound = () => playAudio(clickAudioRef);
  const playWinSound = () => playAudio(winAudioRef);
  const playSlotSound = () => playAudio(slotAudioRef);

  const spinSlots = () => {
    playClickSound();
    playSlotSound();
    setSpinning(true);
    setSpinComplete(false);
    const duration = 3000;
    const interval = 50;
    const startTime = Date.now();

    const spin = () => {
      const elapsedTime = Date.now() - startTime;
      const progress = Math.min(elapsedTime / duration, 1);

      const newSlots = slots.map((_, index) => {
        if (progress >= 1) {
          return letters[index];
        } else {
          return allLetters[Math.floor(Math.random() * allLetters.length)];
        }
      });

      setSlots(newSlots);

      if (progress < 1) {
        requestAnimationFrame(spin);
      } else {
        setSpinning(false);
        setSpinComplete(true);
        setIsExploding(true);
        playWinSound();
        setTimeout(() => {
          setIsExploding(false);
          setCurrentPage('invitation');
        }, 2500);
      }
    };

    requestAnimationFrame(spin);
  };

  const resetGame = () => {
    setSlots(Array(7).fill(''));
    setSpinComplete(false);
    setCurrentPage('slot');
  };

  const addToCalendar = () => {
    const event = {
      text: "Arjun and Navya's Wedding",
      dates: "20240929T110000/20240929T120000",
      location: "Madayi Bank Auditorium, Pazhayangadi, Kannur",
      details: "Wedding Muhoortham: 11AM - 12 PM"
    };
    const calendar = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.text)}&dates=${event.dates}&details=${encodeURIComponent(event.details)}&location=${encodeURIComponent(event.location)}`;
    window.open(calendar, '_blank');
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'slot':
        return (
          <div className="slot-machine-frame p-4 rounded">
            <h1 className="retro-text text-4xl mb-6 text-center text-yellow-300">NEW QUEST!</h1>
            <div className="flex justify-center space-x-1 mb-6 bg-black p-2 rounded">
              {slots.map((letter, index) => (
                <motion.div
                  key={index}
                  className="slot w-12 h-16 sm:w-16 sm:h-24 bg-blue-600 flex items-center justify-center text-3xl sm:text-4xl font-bold text-white"
                  animate={{
                    y: spinning ? [0, -10, 0] : 0,
                  }}
                  transition={{
                    y: {
                      duration: 0.1,
                      repeat: spinning ? Infinity : 0,
                      ease: "linear",
                    },
                  }}
                >
                  {letter}
                </motion.div>
              ))}
            </div>
            <button 
              ref={spinButtonRef}
              onClick={spinSlots} 
              disabled={spinning || spinComplete}
              className="spin-button w-full py-4 text-white text-2xl sm:text-3xl font-bold rounded"
            >
              {spinning ? 'SPINNING' : spinComplete ? 'YOU ARE INVITED!' : 'SPIN!'}
            </button>
          </div>
        );
      case 'invitation':
        return (
          <div className="retro-popup">
            <h2 className="retro-text text-2xl sm:text-2xl text-yellow-300 mb-4">CONGRATULATIONS! </h2>
            <div className="retro-text text-base sm:text-xl text-white space-y-2 sm:space-y-4 sm:grid sm:grid-cols-2 sm:gap-4">
              <p className="sm:col-span-2 mobile-extra-space">YOU'VE BEEN INVITED TO THE WEDDING OF</p>
              <div className="names-container text-yellow-300 text-2xl sm:text-3xl flex items-center justify-center sm:col-span-2 mobile-extra-space">
                <p>ARJUN AND NAVYA<HeartIcon className="w-5 h-5 sm:w-6 sm:h-6 ml-2 text-red-500" /></p>
              </div>
              <p className="mobile-section-space sm:col-span-2">DATE:SEPTEMBER 29TH 2024</p>
              <p className="sm:col-span-2">MUHOORTHAM:</p>
              <p className="sm:col-span-2">11:00AM-12:00PM</p>
              <p className="mobile-venue-space sm:col-span-2">VENUE:MADAYI BANK AUDITORIUM,</p>
              <p className="sm:col-span-2">PAZHAYANGADI, KANNUR</p>
            </div>
            <button onClick={() => {
              playClickSound();
              setCurrentPage('options');
            }} className="retro-button mt-4 sm:mt-6 w-full bg-green-500 hover:bg-green-600 text-black">
              ACCEPT QUEST
            </button>
          </div>
        );
      case 'options':
        return (
          <div className="retro-popup">
            <h2 className="retro-text text-2xl sm:text-3xl text-yellow-300 mb-4">QUEST ACCEPTED!</h2>
            <p className="retro-text text-base sm:text-xl text-white">
              Choose your next action:
            </p>
            <div className="flex flex-col space-y-3 sm:space-y-4 mt-4 sm:mt-6">
              <button onClick={() => {
                playClickSound();
                setCurrentPage('map');
              }} className="retro-button">
                VIEW MAP
              </button>
              <button onClick={() => {
                playClickSound();
                addToCalendar();
              }} className="retro-button">
                ADD TO CALENDAR
              </button>
              <button onClick={() => {
                playClickSound();
                resetGame();
              }} className="retro-button">
                CLOSE
              </button>
            </div>
          </div>
        );
      case 'map':
        return (
          <div className="retro-popup">
            <h2 className="retro-text text-2xl sm:text-3xl text-yellow-300 mb-4">LOCATION</h2>
            <div className="retro-text text-base sm:text-xl text-white space-y-2 sm:space-y-4">
              <p>VENUE: MADAYI BANK AUDITORIUM, PAZHAYANGADI, KANNUR</p>
              <p>NEAREST RAILWAY STATION: PAZHAYANGADI RAILWAY STATION</p>
              <p>NEAREST AIRPORT: KANNUR AIRPORT</p>
            </div>
            <div className="button-container mt-4">
              <button onClick={() => {
                playClickSound();
                setCurrentPage('options');
              }} className="retro-button">
                BACK
              </button>
              <button 
                onClick={() => {
                  playClickSound();
                  window.open('https://maps.app.goo.gl/zhphicHjir2CFnKa8', '_blank');
                }}
                className="retro-button"
              >
                OPEN IN MAPS
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="retro-bg flex flex-col items-center justify-center min-h-screen">
        <div className="loading-text">LOADING...</div>
        <div className="loading-bar"></div>
      </div>
    );
  }

  return (
    <div className="retro-bg flex flex-col items-center justify-center min-h-screen p-4">
      <AudioButton audioEnabled={audioEnabled} isMuted={isMuted} onToggle={toggleAudio} />
      {renderPage()}
      <RetroConfetti isExploding={isExploding} />
    </div>
  );
};

export default SlotMachine;
